import React, { useEffect, useState } from "react";
import { Loader2, Ticket, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";
import { errMsg } from "./hqUtils";

const AUBERGINE  = "var(--hq-deep, #5A4565)";
const PLUM       = "var(--hq-accent, #6F4A7A)";
const PARCH_EDGE = "#EFE4CC";
const GOLD = "#C9A268";

export default function HQCredits() {
  const [data, setData] = useState({ balance: 0, vouchers: [] });
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await customerApi.get("/customer/vouchers");
      setData(r.data || { balance: 0, vouchers: [] });
    } catch {
      setData({ balance: 0, vouchers: [] });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      await customerApi.post("/customer/vouchers/redeem", { code: code.trim() });
      toast.success("Voucher redeemed.");
      setCode("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't redeem."));
    } finally { setRedeeming(false); }
  };

  return (
    <div data-testid="hq-credits" className="space-y-5" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Heritage Headquarters</div>
        <h1 className="text-4xl mt-1.5">Credits &amp; Vouchers</h1>
      </header>

      <div className="bg-white border rounded p-5" style={{ borderColor: PARCH_EDGE }} data-testid="hq-credits-balance">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>Current balance</div>
        <div className="text-4xl mt-1" style={{ color: AUBERGINE }}>NZ${Number(data.balance || 0).toFixed(2)}</div>
      </div>

      <div className="bg-white border rounded p-5 space-y-2" style={{ borderColor: PARCH_EDGE }} data-testid="hq-credits-redeem">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>Redeem a voucher code</div>
        <div className="flex gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="HER-XXXX-XXXX"
            data-testid="hq-credits-code"
            className="flex-1 px-3 py-2 text-sm border rounded outline-none uppercase tracking-widest"
            style={{ borderColor: PARCH_EDGE, color: AUBERGINE }}
          />
          <button
            type="button"
            onClick={redeem}
            disabled={redeeming || !code.trim()}
            data-testid="hq-credits-redeem-btn"
            className="px-4 py-2 rounded text-xs tracking-[0.2em] uppercase font-semibold"
            style={{ backgroundColor: AUBERGINE, color: "#FBF6E9" }}
          >
            {redeeming ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Redeem"}
          </button>
        </div>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-2" style={{ color: PLUM }}>History</div>
        {loading ? (
          <div className="opacity-70"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>
        ) : (data.vouchers || []).length === 0 ? (
          <div className="bg-white border border-dashed rounded p-8 text-center italic opacity-70" style={{ borderColor: PARCH_EDGE }}>
            <Ticket className="w-8 h-8 mx-auto mb-3 opacity-50" />
            No vouchers redeemed yet , when Kayla sends you a gift code, redeem it above.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="hq-credits-list">
            {data.vouchers.map((v, i) => (
              <li key={i} className="bg-white border rounded p-3 flex justify-between items-center" style={{ borderColor: PARCH_EDGE }}>
                <div>
                  <div className="text-sm font-semibold inline-flex items-center gap-1.5">
                    <Ticket className="w-3 h-3" style={{ color: GOLD }} />
                    {v.code || v.id}
                  </div>
                  <div className="text-[10px] opacity-70 mt-0.5">{String(v.redeemed_at || v.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold">NZ${Number(v.amount || 0).toFixed(2)}</div>
                  {v.status && <div className="text-[10px] opacity-70">{v.status}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
